import { Section } from '@vnetwork/ui/layout';
import { SelectableCard } from './SelectableCard';
import {
  Card,
  Checkbox,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import type { JobFormData, OutputKey } from './use-job-create-form';
import styles from './JobCreateOutputSection.module.css';
import tileStyles from './SelectableTile.module.css';

interface Props {
  form: JobFormData;
  presets: { value: string; label: string; description?: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  verimatrixes: any[];
  togglePreset: (v: string) => void;
  isOutputSelected: (k: OutputKey) => boolean;
  toggleOutputCard: (k: OutputKey) => void;
  toggleCardOption: (k: OutputKey, field: string) => void;
  updateOutputOption: (k: OutputKey, field: string, value: unknown) => void;
  updateGlobalOverride: (field: string, value: boolean) => void;
  fieldErrors?: { presets?: string; outputs?: string };
}

const OUTPUT_CARDS: {
  key: OutputKey;
  title: string;
  subtitle: string;
  options: { label: string; field: string }[];
}[] = [
  {
    key: 'hls',
    title: 'HLS',
    subtitle: 'HTTP Live Streaming',
    options: [
      { label: '2-pass encoding', field: 'twoPass' },
      { label: 'Multiplex video + audio', field: 'multiplex' },
      { label: 'Black frame removal', field: 'blackFrameRemoval' },
    ],
  },
  {
    key: 'dash',
    title: 'MPEG-DASH',
    subtitle: 'Dynamic Adaptive Streaming over HTTP',
    options: [
      { label: '2-pass encoding', field: 'twoPass' },
      { label: 'Black frame removal', field: 'blackFrameRemoval' },
    ],
  },
  {
    key: 'mss',
    title: 'MS-SSTR',
    subtitle: 'Microsoft Smooth Streaming',
    options: [
      { label: '2-pass encoding', field: 'twoPass' },
      { label: 'Black frame removal', field: 'blackFrameRemoval' },
    ],
  },
  {
    key: 'mp4',
    title: 'MP4',
    subtitle: 'Progressive file output',
    options: [
      { label: '2-pass encoding', field: 'twoPass' },
      { label: 'Fragmented MP4', field: 'fragmented' },
      { label: 'Black frame removal', field: 'blackFrameRemoval' },
    ],
  },
];

const GLOBAL_OVERRIDES: { field: keyof JobFormData['globalOverrides']; label: string; description: string }[] = [
  { field: 'transmuxOnly', label: 'Transmuxing only', description: 'Bypasses re-encoding (container change only).' },
  { field: 'audioOnly', label: 'Audio only', description: 'Applies to HLS output only.' },
  { field: 'gpuTranscoding', label: 'GPU transcoding', description: 'Enable hardware acceleration.' },
  { field: 'thumbnailSeeking', label: 'Thumbnail seeking', description: 'Generate thumbnail sprites for seeking.' },
];

export function JobCreateOutputSection({
  form,
  presets,
  verimatrixes,
  togglePreset,
  isOutputSelected,
  toggleOutputCard,
  toggleCardOption,
  updateOutputOption,
  updateGlobalOverride,
  fieldErrors,
}: Props) {
  return (
    <Section title="Output Formats" style={{ marginBottom: 0 }}>
      <Card flat className={styles.cardInner}>

        {/* Presets */}
        <div className={styles.block}>
          <p className={styles.blockLabel}>Presets</p>
          <p className={styles.blockHint}>Select one or more preset groups to apply to this job.</p>
          {presets.length === 0 ? (
            <p className={styles.empty}>No preset groups available.</p>
          ) : (
            <div className={styles.presetGrid}>
              {presets.map((preset) => {
                const active = form.presets.includes(preset.value);
                return (
                  <SelectableCard
                    key={preset.value}
                    selected={active}
                    className={`${tileStyles.tile} ${tileStyles.tileDashed} ${styles.presetChip} ${active ? tileStyles.tileActive : ''}`}
                    onSelect={() => togglePreset(preset.value)}
                  >
                    <Checkbox
                      checked={active}
                      onCheckedChange={() => togglePreset(preset.value)}
                      aria-hidden
                      tabIndex={-1}
                      style={{ pointerEvents: 'none', flexShrink: 0 }}
                    />
                    <span>
                      <span className={styles.presetLabel}>{preset.label}</span>
                      {preset.description && (
                        <span className={styles.presetDesc}>{preset.description}</span>
                      )}
                    </span>
                  </SelectableCard>
                );
              })}
            </div>
          )}
          {fieldErrors?.presets && <p className={styles.fieldError}>{fieldErrors.presets}</p>}
        </div>

        {/* Output format cards */}
        <div className={styles.block}>
          <p className={styles.blockLabel}>Output Formats</p>
          <p className={styles.blockHint}>Select one or more output formats.</p>
          <div className={styles.outputGrid}>
            {OUTPUT_CARDS.map((card) => {
              const selected = isOutputSelected(card.key);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const cfg = form.outputOptions[card.key] as any;
              return (
                <div
                  key={card.key}
                  className={`${tileStyles.tile} ${tileStyles.tileDashed} ${styles.outputCard} ${selected ? tileStyles.tileActive : ''}`}
                >
                  <SelectableCard
                    selected={selected}
                    className={styles.outputCardHeader}
                    onSelect={() => toggleOutputCard(card.key)}
                  >
                    <Checkbox
                      checked={selected}
                      onCheckedChange={() => toggleOutputCard(card.key)}
                      aria-hidden
                      tabIndex={-1}
                      style={{ pointerEvents: 'none', flexShrink: 0 }}
                    />
                    <span>
                      <span className={styles.outputTitle}>{card.title}</span>
                      <span className={styles.outputSubtitle}>{card.subtitle}</span>
                    </span>
                  </SelectableCard>

                  <div className={styles.optionList}>
                    {card.options.map((opt) => (
                      <label key={opt.field} className={styles.optionRow}>
                        <Checkbox
                          checked={Boolean(cfg[opt.field])}
                          onCheckedChange={() => toggleCardOption(card.key, opt.field)}
                        />
                        <span className={styles.optionLabel}>{opt.label}</span>
                      </label>
                    ))}
                  </div>

                  {/* Protection — only HLS has DRM options */}
                  <div className={styles.protectionBlock}>
                    <p className={styles.protectionLabel}>Protection</p>
                    {card.key !== 'hls' ? (
                      <p className={styles.protectionNone}>None</p>
                    ) : (
                      <div className={styles.protectionOptions}>
                        {/* None */}
                        <label className={styles.optionRow}>
                          <Checkbox
                            checked={Boolean(cfg.noneEnabled)}
                            onCheckedChange={(v) => {
                              updateOutputOption(card.key, 'noneEnabled', v === true);
                              if (v === true) updateOutputOption(card.key, 'protection', 'none');
                            }}
                          />
                          <span className={styles.optionLabel}>None</span>
                        </label>

                        {/* AES */}
                        <label className={styles.optionRow}>
                          <Checkbox
                            checked={Boolean(cfg.aesEnabled)}
                            onCheckedChange={(v) => {
                              updateOutputOption(card.key, 'aesEnabled', v === true);
                              if (v === true) updateOutputOption(card.key, 'protection', 'aes');
                            }}
                          />
                          <span className={styles.optionLabel}>AES</span>
                        </label>
                        {cfg.aesEnabled && (
                          <div className={styles.subOptions}>
                            <label className={styles.optionRow}>
                              <Checkbox
                                checked={Boolean(cfg.aesKeyRotation)}
                                onCheckedChange={(v) => updateOutputOption(card.key, 'aesKeyRotation', v === true)}
                              />
                              <span className={styles.optionLabel}>Key rotation</span>
                            </label>
                            <Field label="Resource ID">
                              {(p) => (
                                <Input
                                  {...p}
                                  inputSize="sm"
                                  value={cfg.aesResourceId ?? ''}
                                  onChange={(e) => updateOutputOption(card.key, 'aesResourceId', e.target.value)}
                                />
                              )}
                            </Field>
                          </div>
                        )}

                        {/* Verimatrix DRM */}
                        <label className={styles.optionRow}>
                          <Checkbox
                            checked={Boolean(cfg.verimatrixEnabled)}
                            onCheckedChange={(v) => {
                              updateOutputOption(card.key, 'verimatrixEnabled', v === true);
                              if (v === true) updateOutputOption(card.key, 'protection', 'verimatrix');
                            }}
                          />
                          <span className={styles.optionLabel}>Verimatrix DRM</span>
                        </label>
                        {cfg.verimatrixEnabled && (
                          <div className={styles.subOptions}>
                            <label className={styles.optionRow}>
                              <Checkbox
                                checked={Boolean(cfg.verimatrixKeyRotation)}
                                onCheckedChange={(v) => updateOutputOption(card.key, 'verimatrixKeyRotation', v === true)}
                              />
                              <span className={styles.optionLabel}>Key rotation</span>
                            </label>
                            <Field label="Resource ID">
                              {(p) => (
                                <Input
                                  {...p}
                                  inputSize="sm"
                                  value={cfg.verimatrixResourceId ?? ''}
                                  onChange={(e) => updateOutputOption(card.key, 'verimatrixResourceId', e.target.value)}
                                />
                              )}
                            </Field>
                            {verimatrixes.length > 0 && (
                              <Field label="Verimatrix DRM endpoint">
                                {(p) => (
                                  <Select
                                    value={cfg.verimatrixId ?? ''}
                                    onValueChange={(v) => updateOutputOption(card.key, 'verimatrixId', v)}
                                  >
                                    <SelectTrigger size="sm" {...p}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {verimatrixes.map((vm) => (
                                        <SelectItem key={vm.id} value={String(vm.id)}>
                                          {vm.name ?? vm.id}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </Field>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {fieldErrors?.outputs && <p className={styles.fieldError}>{fieldErrors.outputs}</p>}
        </div>

        {/* Global overrides */}
        <div className={styles.block}>
          <p className={styles.blockLabel}>Global Overrides</p>
          <p className={styles.blockHint}>These settings apply to the entire job, across all output formats.</p>
          <div className={styles.overrideGrid}>
            {GLOBAL_OVERRIDES.map(({ field, label, description }) => (
              <label key={field} className={styles.overrideRow}>
                <Checkbox
                  checked={form.globalOverrides[field]}
                  onCheckedChange={(v) => updateGlobalOverride(field, v === true)}
                  className={styles.overrideCheckbox}
                />
                <span>
                  <span className={styles.overrideLabel}>{label}</span>
                  <span className={styles.overrideDesc}>{description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

      </Card>
    </Section>
  );
}
